"use client";
import { useMemo } from "react";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem as RadixSelectItem,
  SelectValue
} from "@cmp-src/primitives/Select";
import { UnifiedSelect, type SelectItem } from "@cmp-src/form/UnifiedSelect";
import { useIsMobile } from "@cmp-src/hooks/use-mobile";
import { ArrowDownIcon } from "@cmp-src/icons/icons";
import { cn } from "@gettactic/helpers";

type Props = {
  officeId: string | null;
  setOfficeId: React.Dispatch<React.SetStateAction<string>>;
  offices: { id: string | null; name: string }[];
  className?: string;
  classNamePrefix?: string;
  readonly?: boolean;
  customIcon?: React.ReactNode;
};

export function OfficeFilterDropdown({
  offices,
  setOfficeId,
  officeId,
  className = "",
  classNamePrefix = "react-select",
  readonly = false,
  customIcon
}: Props) {
  const isMobile = useIsMobile();

  const options = useMemo(() => {
    const officeValues = offices.map((x) => ({ value: x.id, label: x.name }));
    return officeValues;
  }, [offices]);
  const value = options.find((x) => x.value === officeId);

  if (isMobile) {
    return (
      <Select
        value={value?.value || undefined}
        onValueChange={(id) => {
          if (readonly) {
            return;
          }
          setOfficeId(id ?? "");
        }}
        disabled={readonly}
      >
        <SelectTrigger
          variant={"gray"}
          className={cn("mt-1 min-h-[42px]", className)}
        >
          <SelectValue className="truncate" placeholder="Select office" />
          {customIcon || <ArrowDownIcon className="h-4 w-4 opacity-50" />}
        </SelectTrigger>
        <SelectContent>
          {options.map((office) => (
            <RadixSelectItem
              className={"text-nowrap w-full truncate"}
              key={office.value}
              value={office.value || ""}
            >
              {office.label}
            </RadixSelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  const items: SelectItem[] = options.map((opt) => ({
    id: opt.value || "",
    name: opt.label
  }));
  const selectedItem = value
    ? (items.find((item) => item.id === value.value) ?? null)
    : null;

  const triggerContent = (
    <button
      type="button"
      className={cn(
        "flex h-10 min-w-[200px] w-full max-w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-sm overflow-hidden relative border border-neutral-300 bg-white hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
        className
      )}
    >
      <span className="truncate min-w-0 flex-1 text-left">
        <span className="absolute inset-2.5 mr-8 truncate">{selectedItem?.name || "Select office"}</span>
      </span>
      <span className="shrink-0">{customIcon || <ArrowDownIcon className="h-4 w-4 opacity-50" />}</span>
    </button>
  );

  return (
    <UnifiedSelect
      items={items}
      value={selectedItem}
      onSelect={(item) => {
        if (readonly) return;
        setOfficeId(item?.id ?? "");
      }}
      disabled={readonly}
      searchable
      trigger={triggerContent}
    />
  );
}
